import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Edit } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { type MenuSectionFormData } from '@/lib/validations/menu';
import type { MenuSection } from '@/types';

interface SectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: MenuSectionFormData) => Promise<void>;
  section?: MenuSection; // For editing existing sections
  existingSectionNames: string[];
}

/**
 * SectionModal - Modal for adding/editing menu sections
 * Features smooth animations and proper form validation
 */
export const SectionModal: React.FC<SectionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  section,
  existingSectionNames,
}) => {
  const [formData, setFormData] = useState<MenuSectionFormData>({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const isEditing = Boolean(section);
  // Initialize form data when modal opens or section changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: section?.name || '',
        description: section?.description || '',
      });
      setErrors({});
    }
  }, [isOpen, section]);

  const validateField = (field: keyof MenuSectionFormData, value: string) => {
    const newErrors = { ...errors };

    if (field === 'name') {
      // Ensure value is a string and then trim
      const stringValue = typeof value === 'string' ? value : '';
      const trimmedValue = stringValue.trim();
      
      // Check for empty name
      if (!trimmedValue) {
        newErrors.name = 'Section name is required';
      }
      // Check for duplicate names (excluding current section when editing)
      else if (existingSectionNames.includes(trimmedValue) && 
               (!isEditing || trimmedValue !== section?.name)) {
        newErrors.name = 'A section with this name already exists';
      }
      // Check length
      else if (trimmedValue.length > 100) {
        newErrors.name = 'Section name must be less than 100 characters';
      }
      else {
        delete newErrors.name;
      }
    }

    if (field === 'description') {
      const stringValue = typeof value === 'string' ? value : '';
      if (stringValue.length > 500) {
        newErrors.description = 'Description must be less than 500 characters';
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };
  const handleInputChange = (field: keyof MenuSectionFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldFocus = (field: keyof MenuSectionFormData) => {
    // Clear field-specific errors when user focuses (starts typing)
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleInputBlur = (field: keyof MenuSectionFormData, value: string) => {
    validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Ensure formData values are strings
    const nameValue = typeof formData.name === 'string' ? formData.name : '';
    const descriptionValue = typeof formData.description === 'string' ? formData.description : '';
    
    // Validate all fields
    validateField('name', nameValue);
    validateField('description', descriptionValue);
    
    // Check if form is valid - ensure name is a string and trimmed
    const trimmedName = nameValue.trim();
    const hasErrors = Object.values(errors).some(error => error);
    if (hasErrors || !trimmedName) {
      return;
    }

    setLoading(true);
    
    try {
      // Ensure we're passing properly typed data
      await onSave({
        name: nameValue,
        description: descriptionValue,
      });
      onClose();
    } catch (error) {
      console.error('Error saving section:', error);
      // The error will be handled by the parent component
    } finally {
      setLoading(false);
    }
  };

  // Safe form validation check with proper type checking
  const isFormValid = (() => {
    try {
      const name = formData.name;
      if (typeof name !== 'string') return false;
      return name.trim().length > 0 && !Object.values(errors).some(error => error);
    } catch {
      return false;
    }
  })();
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    {isEditing ? (
                      <Edit className="w-5 h-5 text-white" />
                    ) : (
                      <Plus className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {isEditing ? 'Edit Section' : 'Add Section'}
                  </h2>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <Input
                  label="Section Name"
                  placeholder="e.g. Appetizers, Main Course, Beverages"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onFocus={() => handleFieldFocus('name')}
                  onBlur={(e) => handleInputBlur('name', e.target.value)}
                  error={errors.name}
                  required
                  autoFocus
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    onBlur={(e) => handleInputBlur('description', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all duration-200 resize-none text-base"
                    rows={3}
                    placeholder="Brief description of this menu section"
                    maxLength={500}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {(formData.description || '').length}/500 characters
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1"
                    disabled={!isFormValid || loading}
                    loading={loading}
                  >
                    {isEditing ? 'Update Section' : 'Add Section'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};